import {AfterViewInit, Component, OnInit, ViewChild} from '@angular/core';
import {InvoiceService} from '../../invoice.service';
import {ClientService} from '../../client.service';
import {Invoices} from '../../../../../projects/shared-lib/src/lib/models/invoices';
import { HttpParams } from '@angular/common/http';
import {AnalyticsService} from '../../../shared/services/analytics.service';
import {animate, state, style, transition, trigger} from '@angular/animations';
import {lastValueFrom} from 'rxjs';
import {MatPaginator} from '@angular/material/paginator';
import {MatSort} from '@angular/material/sort';
import { MatTableDataSource, MatTable, MatColumnDef, MatHeaderCellDef, MatHeaderCell, MatCellDef, MatCell, MatHeaderRowDef, MatHeaderRow, MatRowDef, MatRow } from '@angular/material/table';
import {Invoice} from '../../../../../projects/shared-lib/src/lib/models/invoice';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import moment from 'moment/moment';
import {provideMomentDateAdapter} from '@angular/material-moment-adapter';
import { MatFormField, MatLabel, MatHint, MatSuffix } from '@angular/material/form-field';
import { MatDateRangeInput, MatStartDate, MatEndDate, MatDatepickerToggle, MatDateRangePicker } from '@angular/material/datepicker';
import { ClientInvoiceHeaderComponent } from '../client-invoice-header/client-invoice-header.component';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { JsonPipe, NgIf } from '@angular/common';

const MY_DATE_FORMATS = {
    parse: { dateInput: 'YYYY/MM/DD' },
    display: {
        dateInput: 'YYYY/MM/DD',
        monthYearLabel: 'MMM YYYY',
        dateA11yLabel: 'LL',
        monthYearA11yLabel: 'MMMM YYYY',
    },
};

@Component({
    selector: 'tw-invoices',
    templateUrl: './invoices.component.html',
    styleUrls: ['./invoices.component.css'],
    providers: [provideMomentDateAdapter(MY_DATE_FORMATS)],
    animations: [
        trigger('detailExpand', [
            state('collapsed,void', style({ height: '0px', minHeight: '0' })),
            state('expanded', style({ height: '*' })),
            transition(
                'expanded <=> collapsed',
                animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)'),
            ),
        ]),
    ],
    imports: [
        MatTable,
        MatColumnDef,
        MatHeaderCellDef,
        MatHeaderCell,
        MatFormField,
        MatLabel,
        MatDateRangeInput,
        FormsModule,
        ReactiveFormsModule,
        MatStartDate,
        MatEndDate,
        MatHint,
        MatDatepickerToggle,
        MatSuffix,
        MatDateRangePicker,
        MatCellDef,
        MatCell,
        ClientInvoiceHeaderComponent,
        MatIconButton,
        MatIcon,
        MatHeaderRowDef,
        MatHeaderRow,
        MatRowDef,
        MatRow,
        MatPaginator,
        JsonPipe,
        TranslatePipe,
        MatButton,
        NgIf,
    ],
})
export class InvoicesComponent implements OnInit, AfterViewInit {
    invoices: Invoices;
    dataSource: MatTableDataSource<Invoice> = new MatTableDataSource();
    columnsToDisplay = ['detail'];
    expandedElement: any | null;
    resultsLength = 0;
    columnSelect: string;

    campaignOne = new FormGroup({
        start: new FormControl(),
        end: new FormControl(),
    });

    @ViewChild(MatPaginator) paginator!: MatPaginator;
    @ViewChild(MatSort) sort!: MatSort;

    constructor(
        private invoiceService: InvoiceService,
        private clientService: ClientService,
        private analyticsService: AnalyticsService,
        public translate: TranslateService,
    ) {}

    async ngOnInit() {
        this.analyticsService.trackInvoicesView();
    }

    ngAfterViewInit() {
        this.paginator.page.subscribe(() => {
            this.loadInvoices().then((r) => r);
        });

        this.loadInvoices().then((r) => r);
    }

    async loadInvoices() {
        const client = this.clientService.getClientLocal();
        const offset = this.paginator.pageIndex * this.paginator.pageSize;
        const limit = this.paginator.pageSize;

        const start = this.campaignOne.get('start')?.value;
        const end = this.campaignOne.get('end')?.value;

        let extraParams = new HttpParams()
            .set('clientId', String(client.id))
            .set('includeTickets', 'true')
            .set('offset', offset.toString())
            .set('limit', limit.toString());

        if (start && end) {
            const startISO = moment(start).startOf('day').toISOString();
            const endISO = moment(end).endOf('day').toISOString();

            extraParams = extraParams.append('createdDate', `gte:${startISO}`);
            extraParams = extraParams.append('createdDate', `lte:${endISO}`);
        }

        try {
            const inv = await lastValueFrom(
                this.invoiceService.getInvoices(extraParams),
            );

            this.invoices = inv;
            this.resultsLength = inv.metadata.totalElements;
            this.dataSource.data = inv.invoices;
            this.paginator._intl.itemsPerPageLabel =
                this.translate.currentLang === 'fr'
                    ? 'Factures par pages:'
                    : 'Invoices per pages:';
            this.paginator._intl.nextPageLabel =
                this.translate.currentLang === 'fr'
                    ? 'Page suivante'
                    : 'Next page';
            this.paginator._intl.previousPageLabel =
                this.translate.currentLang === 'fr'
                    ? 'Page précédente'
                    : 'Previous page';
        } catch (error) {
            console.error('Failed to load invoices:', error);
        }
    }

    getDateFormatString(): string {
        if (this.translate.currentLang === 'fr') {
            return 'YYYY/MM/DD – YYYY/MM/DD';
        }
        return 'YYYY/MM/DD – YYYY/MM/DD';
    }

    detailCell(name: string, element: Invoice) {
        this.columnSelect = name;
        this.expandedElement =
            this.expandedElement === element ? null : element;
    }

    applyDateFilter() {
        this.paginator.pageIndex = 0;
        this.loadInvoices();
    }

    clearDateFilter() {
        this.campaignOne.reset();
        this.paginator.pageIndex = 0;
        this.loadInvoices();
    }

    canApplyDateFilter(): boolean {
        const start = this.campaignOne.get('start')?.value;
        const end = this.campaignOne.get('end')?.value;
        return !!start && !!end;
    }

    hasActiveDateFilter(): boolean {
        return (
            !!this.campaignOne.get('start')?.value ||
            !!this.campaignOne.get('end')?.value
        );
    }
}
